/**
 * Drives animation playback for a slide.
 *
 * The playhead is a single integer: the index of the last click group that has played.
 * -1 means nothing has played, so every animated object is hidden. Advancing increments it.
 * Rendering derives entirely from that integer plus the timeline, so playback state stays
 * reconstructible after a re-render and needs no per-object mutable state.
 *
 * Returns a `stateFor(targetId, subTargetId)` lookup that every renderer calls to find out
 * whether it should be hidden, currently playing (and with what style), or fully shown.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildTimeline, collectAnimatedKeys, animatedKey,
} from '../utils/animationUtils';
import { animationStyle } from '../utils/animationCss';

/**
 * @param {Object} slideData    the current slide (object maps + animations)
 * @param {boolean} enabled     play animations (present mode / preview) vs show everything
 * @param {string} slideKey     current slide id; changing it rewinds playback
 * @returns {{
 *   stateFor: (targetId: string, subTargetId?: string) => { state: string, style: Object },
 *   advance: () => boolean,   // true if a group was played, false if the slide is finished
 *   rewind: () => void,
 *   playAll: () => void,
 *   stepIndex: number,
 *   groupCount: number,
 *   isFinished: boolean,
 * }}
 */
export const useAnimationPlayback = (slideData, enabled, slideKey) => {
  const [stepIndex, setStepIndex] = useState(-1);
  // Groups whose animation has finished painting; they render as plain 'shown' so a
  // re-render doesn't restart their keyframes.
  const [settled, setSettled] = useState(() => new Set());
  const timersRef = useRef([]);

  const timeline = useMemo(
    () => (slideData ? buildTimeline(slideData.animations, slideData) : []),
    [slideData]
  );

  const animatedKeys = useMemo(() => collectAnimatedKeys(timeline), [timeline]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const rewind = useCallback(() => {
    clearTimers();
    setStepIndex(-1);
    setSettled(new Set());
  }, [clearTimers]);

  // Changing slide (or entering/leaving present mode) rewinds to the start.
  useEffect(() => {
    rewind();
  }, [slideKey, enabled, rewind]);

  useEffect(() => clearTimers, [clearTimers]);

  /** Play the next click group. Returns false when the slide has no more groups. */
  const advance = useCallback(() => {
    const next = stepIndex + 1;
    if (next >= timeline.length) return false;

    setStepIndex(next);
    // Mark the group settled once its longest step has finished, so later re-renders
    // don't replay it. `both` fill-mode already holds the end state visually.
    const timer = setTimeout(() => {
      setSettled((prev) => {
        const updated = new Set(prev);
        updated.add(next);
        return updated;
      });
    }, timeline[next].duration + 30);
    timersRef.current.push(timer);
    return true;
  }, [stepIndex, timeline]);

  /**
   * Step back one click group, the way PowerPoint's Back does. Earlier groups are marked
   * settled so they render in their finished state rather than replaying.
   */
  const stepBack = useCallback(() => {
    if (stepIndex < 0) return false;
    clearTimers();
    const previous = stepIndex - 1;
    setStepIndex(previous);
    setSettled(new Set(Array.from({ length: Math.max(0, previous + 1) }, (_, i) => i)));
    return true;
  }, [stepIndex, clearTimers]);

  /** Jump to the fully-played state without animating (used when leaving present mode). */
  const playAll = useCallback(() => {
    clearTimers();
    setStepIndex(timeline.length - 1);
    setSettled(new Set(timeline.map((_, i) => i)));
  }, [clearTimers, timeline]);

  // Auto-play a leading group that isn't click-triggered, matching PowerPoint's behavior
  // of running 'With/After Previous' effects as soon as the slide appears.
  useEffect(() => {
    if (!enabled || stepIndex !== -1 || !timeline.length) return;
    const firstStep = timeline[0].steps[0];
    if (firstStep && firstStep.trigger !== 'onClick') advance();
  }, [enabled, stepIndex, timeline, advance]);

  /**
   * Look up the render state for one object (or one SmartArt node).
   * Objects with no animation are always 'shown'.
   */
  const stateFor = useCallback(
    (targetId, subTargetId = null) => {
      if (!enabled) return { state: 'shown', style: {} };

      const key = animatedKey(targetId, subTargetId);
      if (!animatedKeys.has(key)) return { state: 'shown', style: {} };

      for (let groupIndex = 0; groupIndex < timeline.length; groupIndex += 1) {
        const match = timeline[groupIndex].steps.find((step) =>
          step.subTargetIds && step.subTargetIds.length
            ? step.targetId === targetId && step.subTargetIds.includes(subTargetId)
            : step.targetId === targetId && !subTargetId
        );
        if (!match) continue;

        if (groupIndex > stepIndex) return { state: 'hidden', style: animationStyle('hidden', match) };
        if (settled.has(groupIndex)) return { state: 'shown', style: {} };
        return { state: 'playing', style: animationStyle('playing', match) };
      }
      return { state: 'shown', style: {} };
    },
    [enabled, animatedKeys, timeline, stepIndex, settled]
  );

  return {
    stateFor,
    advance,
    stepBack,
    rewind,
    playAll,
    stepIndex,
    groupCount: timeline.length,
    /** Wall-clock ms each click group takes, so a preview can chain them realistically. */
    groupDurations: useMemo(() => timeline.map((g) => g.duration), [timeline]),
    isFinished: stepIndex >= timeline.length - 1,
  };
};

export default useAnimationPlayback;
